import React, { useState, useContext } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase/firebase";
import "./Login.css"; // 🧹 Clean import

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleToken, setGoogleToken] = useState(null);
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);

  const navigate = useNavigate();
  const { login } = useContext(UserContext);

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const idToken = await user.getIdToken(true);

      const response = await axios.post(
        "https://bookmatchup.onrender.com/api/auth/google-login",
        {
          token: idToken,
        }
      );

      if (response.data.requiresUsername) {
        setGoogleToken(idToken);
        setShowUsernamePrompt(true);
      } else {
        const { token, id, username } = response.data;
        localStorage.setItem("token", token);
        login({ username, id, token });
        navigate("/");
      }
    } catch (error) {
      console.error("❌ Google Sign-in Error:", error);
      setError(
        error.response?.data?.message ||
          "Google sign-in failed. Please try again."
      );
    }
  };

  const handleUsernameSubmit = async () => {
    if (!username.trim()) {
      setError("Please choose a valid username.");
      return;
    }

    try {
      const response = await axios.post(
        "https://bookmatchup.onrender.com/api/auth/google-login",
        {
          token: googleToken,
          username,
        }
      );

      if (response.status === 200) {
        const { token, id, username } = response.data;
        localStorage.setItem("token", token);
        login({ username, id, token });
        navigate("/");
      }
    } catch (error) {
      console.error("❌ Error submitting username:", error);
      setError("Username already taken. Try another.");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        "https://bookmatchup.onrender.com/api/auth/login",
        { username, password }
      );

      if (response.status === 200) {
        const { token, id } = response.data;
        localStorage.setItem("token", token);
        login({ username, id, token });
        navigate("/");
      } else {
        setError(response.data.message || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("❌ Login failed:", error);
      setError("Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="void"></div>

      <h1 className="login-title">Login</h1>

      {error && <div className="error-message">{error}</div>}

      {showUsernamePrompt ? (
        <div className="username-prompt">
          <h2>Create Your Username</h2>
          <input
            type="text"
            className="username-input"
            placeholder="Choose a unique username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <button
            className="username-submit-button"
            onClick={handleUsernameSubmit}
            disabled={!username.trim()}
          >
            Confirm & Continue
          </button>
        </div>
      ) : (
        <>
          {/* Standard Login Form */}
          <form className="login-form" onSubmit={handleLogin}>
            <div className="input-container">
              <label htmlFor="username" className="input-label">
                Username
              </label>
              <input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="login-input"
              />
            </div>

            <div className="input-container">
              <label htmlFor="password" className="input-label">
                Password
              </label>
              <div className="password-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="login-input"
                />
                <span
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FaEye className="password-icon" />
                  ) : (
                    <FaEyeSlash className="password-icon" />
                  )}
                </span>
              </div>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>

          <p className="login-note">
            Don't have an account?{" "}
            <Link to="/register" className="link">
              Register here
            </Link>
            .
          </p>
          <p className="login-note">
            Forgot password?{" "}
            <Link to="/forgot-password" className="link">
              Reset here
            </Link>
            .
          </p>

          <div className="void"></div>
          <div className="divider"></div>
          <div className="micro-void"></div>

          {/* Google Login Button */}
          <div className="login-form">
            <button
              className="google-signin-button"
              onClick={handleGoogleSignIn}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                alt="Google Logo"
                className="google-icon"
              />
              Sign in with Google
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;
